import { Finance } from 'financejs';
import { loan } from './loanjs';
export function cumsum(a) {
    return a.reduce((r, v, i) => {
        return r.length === 0 ? [v] : [...r, r[i - 1] + v];
    }, []);
}
export function calculate({ airbnbFee = 0, furnishingCosts = 0, internetCableFeeYearly = 0, lawnCareFeeYearly = 0, suppliesFeeYearly = 0, poolMaintenanceYearly = 0, appreciation, buildingPercentagePropertyValue, contractServices, downPayment, expenseGrowth, hoa, insurance, interestRate, isCalifornia, marginalTaxRate, numberOfUnits, otherExpenses, propertyManagementRateFee, propertyTaxRate, purchasePrice, renovationCost = 0, renovationValueIncrease = 0, rentGrowth, repairsAndMaintenance, term = 30, utilities, vacancyRate = 0, // default to zero for STR
yearOneMonthlyRevenue, yearOneOtherMonthlyRevenue, }) {
    // added furnishing costs
    const totalCashInvested = calcTotalCashInvested(downPayment, renovationCost, furnishingCosts);
    const grossRevenue = calcGrossRent(yearOneMonthlyRevenue, yearOneOtherMonthlyRevenue, rentGrowth);
    const { vacancyRentLoss, operatingIncome } = calcOperatingIncome(grossRevenue, vacancyRate);
    const propertyValue = calcPropertyValue(purchasePrice, renovationValueIncrease, appreciation, term);
    const totalAppreciation = calcTotalAppreciation(propertyValue, purchasePrice);
    const principal = purchasePrice - downPayment;
    const debt = calcDebt(principal, interestRate, term);
    const debtPayment = debt.yearlyInstallments.map(({ installment }) => installment);
    const debtInterest = debt.yearlyInstallments.map(({ interest }) => interest);
    const debtPrincipal = debt.yearlyInstallments.map(({ capital }) => capital);
    const debtPrincipalAccrued = cumsum(debtPrincipal);
    const loanBalance = debtPrincipalAccrued.map((v) => principal - v < 0 ? 0 : principal - v);
    const totalEquity = calcTotalEquity(propertyValue, loanBalance);
    const income = calcIncome(yearOneMonthlyRevenue, yearOneOtherMonthlyRevenue, rentGrowth, vacancyRate);
    const propertyTaxes = calcPropertyTaxes(purchasePrice, propertyTaxRate, isCalifornia ? 0.02 : appreciation);
    const annualDepreciation = calcAnnualDepreciation(purchasePrice, buildingPercentagePropertyValue, term);
    const { insurancePerUnitYear, utilitiesPerUnitYear, contractServicesPerYear, repairsAndMaintenancePerUnitYear, propertyManagementPerYear, hoaPerYear, otherExpensesPerYear, internetCablePerYear, lawnCarePerYear, suppliesPerYear, poolMaintenancePerYear, airbnbPerYear, totalExpenses, } = calcTotalExpenses(income, insurance, utilities, contractServices, repairsAndMaintenance, propertyManagementRateFee, hoa, otherExpenses, numberOfUnits, expenseGrowth, airbnbFee, internetCableFeeYearly, lawnCareFeeYearly, suppliesFeeYearly, poolMaintenanceYearly);
    const operatingExpenses = calcOperatingExpenses(totalExpenses, propertyTaxes);
    const totalDeductions = calcTotalDeductions(operatingExpenses, debtInterest, annualDepreciation);
    const netOperatingIncome = calcNetOperatingIncome(operatingIncome, operatingExpenses);
    const cashFlow = calcCashFlow(netOperatingIncome, debtPayment);
    const { interestTaxBenefits, propertyTaxBenefits, totalTaxBenefits } = calcTaxBenefits(annualDepreciation, debtInterest, propertyTaxes, marginalTaxRate);
    const incomeTax = calcIncomeTax(income, totalExpenses, marginalTaxRate);
    const netCashFlow = calcNetCashFlow(income, propertyTaxes, debtPayment, totalExpenses);
    const netCashFlowCumSum = cumsum(netCashFlow);
    const postTaxCashFlow = calcPostTaxCashFlow(netCashFlow, incomeTax, totalTaxBenefits);
    const totalReturns = calcTotalReturns(netCashFlowCumSum, totalAppreciation, debtPrincipalAccrued);
    const totalProfit = calcTotalProfit(totalEquity, netCashFlowCumSum, totalCashInvested);
    const irr = getIRR(netCashFlow, totalAppreciation, debtPrincipalAccrued, downPayment, renovationCost, furnishingCosts, 10);
    // added furnishing costs for STR
    const capRate = getCapRate(income, totalExpenses, propertyTaxes, purchasePrice, renovationCost, furnishingCosts);
    const yearOneReturn = getYearOneReturn(netCashFlow, downPayment, renovationCost, furnishingCosts);
    const profitYearTen = getProfit(totalReturns, 10);
    //TODO: nest all of them under some section ???
    return {
        furnishingCosts,
        income,
        incomeTax,
        irr,
        cashFlow,
        netCashFlow,
        netCashFlowCumSum,
        postTaxCashFlow,
        profitYearTen,
        propertyTaxes,
        totalAppreciation,
        totalReturns,
        propertyValue,
        yearOneReturn,
        capRate,
        grossRevenue,
        vacancyRentLoss,
        operatingIncome,
        netOperatingIncome,
        totalEquity,
        totalCashInvested,
        totalProfit,
        debt: {
            debtPayment,
            debtInterest,
            debtPrincipal,
            debtPrincipalAccrued,
            loanBalance, // property value -> loan balance
        },
        expenses: {
            airbnbPerYear,
            contractServicesPerYear,
            hoaPerYear,
            insurancePerUnitYear,
            internetCablePerYear,
            lawnCarePerYear,
            operatingExpenses,
            otherExpensesPerYear,
            poolMaintenancePerYear,
            propertyManagementPerYear,
            repairsAndMaintenancePerUnitYear,
            suppliesPerYear,
            utilitiesPerUnitYear,
            totalExpenses, // cash flow -> total expenses; does not include property tax
        },
        taxBenefits: {
            annualDepreciation,
            interestTaxBenefits,
            propertyTaxBenefits,
            totalTaxBenefits,
            totalDeductions,
        },
    };
}
export function calcTotalCashInvested(downPayment, renovationCost, furnishingCosts = 0) {
    return [...Array(30).fill(downPayment + renovationCost + furnishingCosts)];
}
export function calcGrossRent(yearOneMonthlyRent, yearOneOtherMonthlyIncome, rentGrowth) {
    const yearOneIncome = (yearOneMonthlyRent + yearOneOtherMonthlyIncome) * 12;
    return applyGrowth(yearOneIncome, rentGrowth);
}
export function calcOperatingIncome(grossRevenue, vacancyRate) {
    return grossRevenue.reduce((acc, currVal) => {
        const vacancyRentLoss = currVal * vacancyRate;
        const operatingIncome = currVal - vacancyRentLoss;
        return {
            vacancyRentLoss: [...acc.vacancyRentLoss, vacancyRentLoss],
            operatingIncome: [...acc.operatingIncome, operatingIncome],
        };
    }, { vacancyRentLoss: [], operatingIncome: [] });
}
export function calcTotalEquity(propertyValue, loanBalance) {
    return propertyValue.map((v, idx) => v - loanBalance[idx]);
}
export function getProfit(totalReturns, year = 10) {
    return totalReturns[year - 1];
}
export function getCapRate(income, totalExpenses, propertyTaxes, purchasePrice, renovationCost, furnishingCosts = 0, year = 1) {
    return ((income[year - 1] - totalExpenses[year - 1] - propertyTaxes[year - 1]) /
        (purchasePrice + renovationCost + furnishingCosts));
}
export function getYearOneReturn(netCashFlow, downPayment, renovationCost, furnishingCosts = 0) {
    return netCashFlow[0] / (downPayment + renovationCost + furnishingCosts);
}
export function getIRR(netCashFlow, totalAppreciation, debtPrincipalAccrued, downPayment, renovationCost, furnishingCosts = 0, year = 10) {
    const finance = new Finance();
    const exitValue = netCashFlow[year - 1] +
        totalAppreciation[year - 1] +
        debtPrincipalAccrued[year - 1] +
        downPayment;
    let irrValues = [];
    for (let i = 0; i < year - 1; i++) {
        irrValues = [...irrValues, netCashFlow[i]];
    }
    irrValues = [...irrValues, exitValue];
    return finance.IRR(-downPayment - renovationCost - furnishingCosts, ...irrValues);
}
export function calcTotalReturns(netCashFlowCumSum, totalAppreciation, debtPrincipalAccrued) {
    return netCashFlowCumSum.reduce((res, v, i) => [...res, v + totalAppreciation[i] + debtPrincipalAccrued[i]], []);
}
export function calcTotalProfit(totalEquity, netCashFlowCumSum, totalCashInvested) {
    return totalEquity.map((eq, idx) => eq + netCashFlowCumSum[idx] - totalCashInvested[idx]);
}
export function calcTaxBenefits(annualDepreciation, debtInterest, propertyTaxes, marginalTaxRate) {
    const interestTaxBenefits = debtInterest.map(v => v * marginalTaxRate);
    const propertyTaxBenefits = propertyTaxes.map(v => v * marginalTaxRate);
    const totalTaxBenefits = annualDepreciation.reduce((res, v, i) => [...res, v + interestTaxBenefits[i] + propertyTaxBenefits[i]], []);
    return {
        interestTaxBenefits,
        propertyTaxBenefits,
        totalTaxBenefits,
    };
}
export function calcIncomeTax(income, totalExpenses, marginalTaxRate) {
    return income.reduce((res, v, i) => [...res, (v - totalExpenses[i]) * marginalTaxRate], []);
}
export function calcPostTaxCashFlow(netCashFlow, incomeTax, totalTaxBenefits) {
    return netCashFlow.reduce((res, v, i) => [...res, v - incomeTax[i] + totalTaxBenefits[i]], []);
}
export function calcNetCashFlow(income, propertyTaxes, debtPayment, totalExpenses) {
    const debtAmount = (i) => (debtPayment.length == 0 ? 0 : debtPayment[i]);
    return income.reduce((res, v, i) => [...res, v - propertyTaxes[i] - debtAmount(i) - totalExpenses[i]], []);
}
export function calcTotalExpenses(income, insurance, utilities, contractServices, repairsAndMaintenance, propertyManagementRateFee, hoa, otherExpenses, unitCount, expenseGrowth, 
// str only
airbnbFee = 0, internetCableFee = 0, lawnCareFee = 0, suppliesFee = 0, poolMaintenance = 0) {
    const insurancePerUnitYear = applyGrowth(insurance * unitCount, expenseGrowth);
    const utilitiesPerUnitYear = applyGrowth(utilities * unitCount * 12, expenseGrowth);
    const contractServicesPerYear = applyGrowth(contractServices, expenseGrowth);
    const repairsAndMaintenancePerUnitYear = applyGrowth(repairsAndMaintenance * unitCount, expenseGrowth);
    const propertyManagementPerYear = income.map(v => v * propertyManagementRateFee);
    const hoaPerYear = applyGrowth(hoa * 12, expenseGrowth);
    const otherExpensesPerYear = applyGrowth(otherExpenses, expenseGrowth);
    const internetCablePerYear = applyGrowth(internetCableFee, expenseGrowth);
    const lawnCarePerYear = applyGrowth(lawnCareFee, expenseGrowth);
    const suppliesPerYear = applyGrowth(suppliesFee, expenseGrowth);
    const poolMaintenancePerYear = applyGrowth(poolMaintenance, expenseGrowth);
    const airbnbPerYear = income.map(v => v * airbnbFee);
    const totalExpenses = insurancePerUnitYear.reduce((res, insurance, i) => {
        return [
            ...res,
            insurance +
                utilitiesPerUnitYear[i] +
                contractServicesPerYear[i] +
                repairsAndMaintenancePerUnitYear[i] +
                propertyManagementPerYear[i] +
                hoaPerYear[i] +
                otherExpensesPerYear[i] +
                // just for STR
                internetCablePerYear[i] +
                lawnCarePerYear[i] +
                suppliesPerYear[i] +
                poolMaintenancePerYear[i] +
                airbnbPerYear[i],
        ];
    }, []);
    return {
        insurancePerUnitYear,
        utilitiesPerUnitYear,
        contractServicesPerYear,
        repairsAndMaintenancePerUnitYear,
        propertyManagementPerYear,
        hoaPerYear,
        otherExpensesPerYear,
        internetCablePerYear,
        lawnCarePerYear,
        suppliesPerYear,
        poolMaintenancePerYear,
        airbnbPerYear,
        totalExpenses,
    };
}
export function calcOperatingExpenses(totalExpenses, propertyTaxes) {
    return totalExpenses.reduce((res, exp, i) => [...res, exp + propertyTaxes[i]], []);
}
export function calcNetOperatingIncome(operatingIncome, operatingExpenses) {
    return operatingIncome.map((inc, idx) => inc - operatingExpenses[idx]);
}
export function calcCashFlow(netOperatingIncome, debtPayment) {
    if (debtPayment.length == 0)
        return netOperatingIncome;
    return netOperatingIncome.map((inc, idx) => inc - debtPayment[idx]);
}
export function calcIncome(yearOneMonthlyRent, yearOneOtherMonthlyIncome, rentGrowth, vacancyRate) {
    const annualTotalRent = 12 * (yearOneMonthlyRent + yearOneOtherMonthlyIncome);
    const initialIncome = annualTotalRent * (1 - vacancyRate);
    return applyGrowth(initialIncome, rentGrowth);
}
export function calcPropertyTaxes(purchasePrice, propertyTaxRate, propertyTaxRateGrowth) {
    return applyGrowth(purchasePrice * propertyTaxRate, propertyTaxRateGrowth);
}
/** Applies a growth rate to an initial value over `term` years */
export function applyGrowth(initialValue, growthRate, firstYearGrowth = false, term = 30) {
    let res = [];
    if (firstYearGrowth) {
        // [initialValue * (1+rate), initialValue * (1+rate)^2, initialValue * (1+rate)^3),....]
        for (let i = 0; i < term; i++) {
            res = [...res, initialValue * (1 + growthRate) ** (i + 1)];
        }
    }
    else {
        for (let i = 0; i < term; i++) {
            res = [...res, initialValue * (1 + growthRate) ** i];
        }
    }
    return res;
}
/** Calculates property value line items */
export function calcPropertyValue(purchasePrice, renovationValueIncrease, appreciation, term = 30) {
    return applyGrowth(purchasePrice + renovationValueIncrease, appreciation, true, term);
}
export function calcTotalAppreciation(value, purchasePrice) {
    let res = [];
    return value.reduce((r, v) => [...r, v - purchasePrice], res);
}
/** Calculate all debt line items */
export function calcDebt(principal, interestRate, term = 30) {
    return loan(principal, term * 12, interestRate);
}
export function calcDepreciationValue(purchasePrice, buildingPercentagePropertyValue, residentialUsefulLife = 27.5) {
    return (purchasePrice * buildingPercentagePropertyValue) / residentialUsefulLife;
}
export function calcAnnualDepreciation(purchasePrice, buildingPercentagePropertyValue, term) {
    const depreciation = calcDepreciationValue(purchasePrice, buildingPercentagePropertyValue);
    const res = [];
    for (let i = 0; i < term; i++) {
        if (i < 27)
            res.push(depreciation);
        else if (i === 27)
            res.push(depreciation / 2.0);
        else
            res.push(0);
    }
    return res;
}
export function calcTotalDeductions(operatingExpenses, debtInterest, annualDepreciation) {
    return operatingExpenses.reduce((acc, exp, i) => {
        return [...acc, exp + debtInterest[i] + annualDepreciation[i]];
    }, []);
}
//# sourceMappingURL=index.js.map